<template>
  <div class="container-fluid payment-panel">
    <button class="payment-button save-button" :style="saveColor" :disabled="disabled" @click="saveTable">
      <font-awesome-icon icon="fa-solid fa-save" class="button-icon"></font-awesome-icon>
      SAVE
    </button>

    <button :disabled="disabled || hasInvalidQuantity" 
            class="payment-button pay-button" 
            @click="validateAndProcess" 
            :style="color">
      <font-awesome-icon icon="fa-solid fa-credit-card" class="button-icon"></font-awesome-icon>
      PAY
      <span class="amount">{{ $utils.formatMoney(saleTotal - discount + tipsTotal, 2, '.', ',', false) }}</span>
      <span v-if="hasInvalidQuantity" class="error-message">{{ quantityErrorMessage }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: "PaymentControls",
  data() {
    return {
      hasInvalidQuantity: false,
      quantityErrorMessage: '',
      invalidLine: null
    }
  },
  watch: {
    'sale.items': {
      handler() {
        this.validateQuantities();
      },
      deep: true,
      immediate: true
    },
    '$store.getters.getSaleLines': {
      handler(newLines) {
        if (newLines) {
          this.validateQuantities();
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.validateQuantities();
  },
  computed: {
    saleLines() {
      return this.$store.getters.getSaleLines;
    },
    sale() {
      return this.$store.getters.getCurrentSale;
    },
    discount() {
      return this.$store.getters.getSaleDiscount.total;
    },
    saleTotal() {
      return this.$store.getters.getSaleTotal
    },
    tipsTotal() {
      return this.$store.getters.getTipAmount;
    },
    disabled() {
      return this.saleTotal === 0 || this.hasInvalidQuantity;
    },
    color() {
      if (this.hasInvalidQuantity) {
        return {background: '#dc3545'};
      }
      return this.disabled ? {background: 'grey'} : {background: '#9ccc65'}
    },

    delivery() {
      return this.disabled ? {background: 'grey'} : {background: '#37b1c6'}
    },

    saveColor() {
      return this.disabled ? {background: 'grey'} : {background: '#475f9b'}
    }
  },
  methods: {
    validateQuantities() {
      const saleLines = this.saleLines;
      const minQty = 0.01;
      
      if (!saleLines || saleLines.length === 0) {
        this.hasInvalidQuantity = false;
        this.quantityErrorMessage = '';
        this.invalidLine = null;
        return true;
      }
      
      for (const line of saleLines) {
        const qty = parseFloat(line.Qty);
        if (isNaN(qty) || qty <= 0) {
          this.hasInvalidQuantity = true;
          this.quantityErrorMessage = `Invalid quantity for ${line.Description}`;
          this.invalidLine = line;
          return false;
        }
        if (qty < minQty) {
          this.hasInvalidQuantity = true;
          this.quantityErrorMessage = `Minimum quantity (${minQty}) required for ${line.Description}`;
          this.invalidLine = line;
          return false;
        }
      }
      
      this.hasInvalidQuantity = false;
      this.quantityErrorMessage = '';
      this.invalidLine = null;
      return true;
    },

    validateAndProcess() {
      if (this.validateQuantities()) {
        this.process();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Quantity',
          html: `
            <div class="invalid-quantity-alert">
              <p>${this.quantityErrorMessage}</p>
              <p class="highlight-text">Please check the quantity for: <strong>${this.invalidLine.Description}</strong></p>
              <p class="current-value">Current quantity: <strong>${this.invalidLine.Qty}</strong></p>
            </div>
          `,
          confirmButtonColor: '#dc3545',
          customClass: {
            container: 'invalid-quantity-modal'
          }
        });
      }
    },

    saveTable() {
      if (!this.validateQuantities()) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Quantity',
          html: `
            <div class="invalid-quantity-alert">
              <p>${this.quantityErrorMessage}</p>
              <p class="highlight-text">Please check the quantity for: <strong>${this.invalidLine.Description}</strong></p>
              <p class="current-value">Current quantity: <strong>${this.invalidLine.Qty}</strong></p>
            </div>
          `,
          confirmButtonColor: '#dc3545',
          customClass: {
            container: 'invalid-quantity-modal'
          }
        });
        return;
      }

      let table = this.$store.getters.getTableBySaleNum(this.sale);

      if (table) {
        return this.editTable(table);
      } else {
        return this.createTable();
      }
    },
    loadPanel() {
      return Swal.fire({
        html: 'Updating table....',
        allowOutsideClick: false,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading()
        },
      });
    },
    editTable() {
      let settings = this.$store.getters.getDeviceSettings;
      let table = {};
      this.loadPanel()
      table.items = this.$store.getters.getSaleLines;
      table.Customer = this.$store.getters.getCustomer;
      table.name = this.$store.getters.getOpenTableName;
      return this.$store.dispatch('updateTable', table)
          .then(() => {
            responses.showInfo('Table successfully updated')
            let autoLogout = settings.AutoLog === 1 || settings.PosType === `WaiterStation`;
            if (autoLogout) {
              return this.$router.push({ name : `poslogin` });
            }
            this.$store.commit('setOpenTableName', null);
            this.$store.commit('setIsTabOpen',false)
            return this.$router.push({ name : `tables` })
          }).finally(() => Swal.close())
    },
    createTable() {
      this.$store.commit('setIsTabOpen', true)
      return Swal.fire({
        title: 'Quotation Name',
        icon: 'question',
        input: 'text',
        showCancelButton: true,
        confirmButtonText: 'Save Quotation',
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        preConfirm: (input) => {
          if (!input) return Swal.showValidationMessage(`Quotation name is required`)
          this.$store.commit(`setOpenTableName` , input)
          return this.$store.dispatch('saveNewTable', {
            name : input,
            Customer: this.$store.getters.getCustomer,
          }).then(() => input)
              .catch(error => Swal.showValidationMessage(error))
        },
      }).then((result) => {
        this.$store.commit('setIsTabOpen',false)
        if (result.isConfirmed) {
          this.$store.commit('setOpenTableName', null);
          return Swal.fire({
            title: `Success`,
            icon: 'success',
            text: `Quotation ${result.value} Saved`
          });
        }
      })
    },

    process() {
      return this.$router.push({name: 'payment'})
    },
    processDelivery() {
      this.$store.commit(`setTipAmount` , 0)
      return this.process();
    },
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
  .view-order-button {
    opacity: 1 !important;
  }
}

.payment-panel {
  -webkit-box-shadow: 0 -2px 8px 0 rgba(203, 153, 126, 0.4);
  box-shadow: 0 -2px 8px 0 rgba(203, 153, 126, 0.4);
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-direction: row;
  padding: 15px;
  z-index: 1;
  background: white;

  .payment-button {
    color: white;
    font-size: 18px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    border-radius: 8px;
    height: 100%;
    width: 60%;
    max-width: 400px;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 20px;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    .button-icon {
      font-size: 20px;
    }

    .amount {
      font-size: 16px;
      font-weight: 500;
      margin-left: 8px;
    }
  }

  .save-button {
    background: #475f9b;
    &:hover:not(:disabled) {
      background: #3a4d7d;
    }
  }

  .pay-button {
    background: #9ccc65;
    &:hover:not(:disabled) {
      background: #8abf54;
    }
  }

  .error-message {
    font-size: 12px;
    margin-top: 4px;
    color: #fff;
    text-align: center;
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
  }
}

:deep(.invalid-quantity-modal) {
  .invalid-quantity-alert {
    text-align: left;
    padding: 10px 0;

    p {
      margin: 8px 0;
      font-size: 14px;
    }

    .highlight-text {
      color: #dc3545;
      font-weight: 500;
      margin: 12px 0;
    }

    .current-value {
      background: #f8f9fa;
      padding: 8px;
      border-radius: 4px;
      border-left: 3px solid #dc3545;
    }

    strong {
      color: #dc3545;
    }
  }
}
</style>
